import React, { useCallback } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { useNavigate } from "react-router-dom";
import { isNil } from "ramda";

import { AffinityForm, ConfirmationView, FormView } from "affinityForm";
import { translateErrorMessages, useDryRunMutation, useSubmitMutation } from "affinityForm/helpers";
import { Layout } from "affinityUI/structures/layout";
import { UserConfirmation, UserForm } from "components/views/userEdit/";
import type { UserInfoCreateType } from "components/views/userEdit/types";
import { userValidationErrors } from "components/views/userEdit/userForm/messages";
import { userInfoValidationSchema } from "components/views/userEdit/userForm/validationSchema";
import { CreateUser, useGetRolesQuery } from "gql";
import { collectGarbage, composeUpdates, invalidateUsersList } from "gql/invalidations";

const varSetter = (user: UserInfoCreateType) => ({
  user,
});

export const AddUserPage: React.FC = () => {
  const { showBoundary: onError } = useErrorBoundary();
  const { data: rolesData, loading: loadingRoles } = useGetRolesQuery({ onError });
  const roles = rolesData?.roles;
  const navigate = useNavigate();
  const update = composeUpdates([invalidateUsersList, collectGarbage]);

  const validateUser = useDryRunMutation(CreateUser, {
    varSetter,
    dataGetter: (response) => response?.data?.createUser,
    parseError: translateErrorMessages(userValidationErrors),
  });

  const createUser = useSubmitMutation(CreateUser, {
    varSetter,
    dataGetter: (response) => response?.data?.createUser,
    update,
    onSave: "/users",
  });

  const handleCancel = useCallback(() => {
    navigate(`/users`);
  }, [navigate]);

  return (
    <AffinityForm
      validationSchema={userInfoValidationSchema}
      asyncValidation={validateUser}
      confirmHandler={createUser}
    >
      <FormView>
        <Layout title="Invite user" loading={loadingRoles} backLink="/users" showBackBtn>
          {!isNil(roles) && <UserForm onCancel={handleCancel} roles={roles} />}
        </Layout>
      </FormView>
      <ConfirmationView>
        <Layout title="Confirm and add user" backLink=".." showBackBtn>
          {!isNil(roles) && <UserConfirmation onCancel={handleCancel} roles={roles} />}
        </Layout>
      </ConfirmationView>
    </AffinityForm>
  );
};
